/* Raw Form cart drawer.

   Opens when the cart link is clicked and when anything adds to the cart, and
   re-renders itself from the Cart AJAX API after every change. It intercepts
   the product form so adding no longer leaves the page.

   Everything degrades: with JS off the cart link is still an ordinary link to
   /cart, and the add-to-cart form still posts normally. */
class RFCartDrawer extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-drawer-body]');
    this.panel = this.querySelector('.rf-drawer__panel');

    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-close]')) {
        e.preventDefault();
        this.close();
        return;
      }
      const btn = e.target.closest('[data-qty]');
      if (btn) {
        e.preventDefault();
        this.change(btn.dataset.line, parseInt(btn.dataset.to, 10));
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.hidden) this.close();
    });

    /* the header cart link opens the drawer instead of navigating */
    document.querySelectorAll('.rf-header__cart').forEach((a) => {
      a.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    this.interceptAdds();
  }

  /* Any product form on the page adds without a page load, then opens here. */
  interceptAdds() {
    document.querySelectorAll('form[action$="/cart/add"]').forEach((form) => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('[name="add"]');
        if (btn) btn.setAttribute('aria-busy', 'true');
        try {
          /* URL-encoded, not multipart: this store drops selling_plan from a
             multipart (or JSON) add, so an Autoship pick reached the cart as a
             one-time purchase. The same fields URL-encoded keep the plan. */
          const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams(new FormData(form))
          });
          if (!res.ok) throw new Error(res.status);
          await this.refresh();
          this.open();
        } catch (err) {
          form.submit();
        } finally {
          if (btn) btn.removeAttribute('aria-busy');
        }
      });
    });
  }

  async change(line, quantity) {
    if (!line || this.busy) return;
    this.busy = true;
    this.setAttribute('data-busy', '');
    try {
      const res = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity })
      });
      if (!res.ok) throw new Error(res.status);
      await this.refresh();
    } catch (err) {
      window.location.assign(window.Shopify.routes.root + 'cart');
    } finally {
      this.busy = false;
      this.removeAttribute('data-busy');
    }
  }

  /* Pull the drawer's own markup back from the server rather than rebuilding
     the DOM by hand, so there is one source of truth for how a line looks. */
  async refresh() {
    const res = await fetch(window.Shopify.routes.root + '?sections=rf-cart-drawer', {
      headers: { Accept: 'application/json' }
    });
    if (!res.ok) return;
    const data = await res.json();
    const markup = data['rf-cart-drawer'];
    if (!markup) return;
    const fresh = new DOMParser().parseFromString(markup, 'text/html').querySelector('rf-cart-drawer');
    if (!fresh) return;

    const wasOpen = !this.hidden;
    this.innerHTML = fresh.innerHTML;
    this.body = this.querySelector('[data-drawer-body]');
    this.panel = this.querySelector('.rf-drawer__panel');
    if (wasOpen) this.hidden = false;

    const count = this.countFromTitle();
    /* Update the count elements only — rewriting the link's text would wipe
       out the mobile bag icon. */
    if (count !== null) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => (el.textContent = count));
      document.querySelectorAll('.rf-header__cart').forEach((a) =>
        a.setAttribute('aria-label', 'Cart, ' + count + (count === 1 ? ' item' : ' items'))
      );
    }
    document.dispatchEvent(new CustomEvent('rf:cart'));
  }

  countFromTitle() {
    const t = this.querySelector('.rf-drawer__title');
    if (!t) return null;
    const m = t.textContent.match(/\((\d+)\)/);
    return m ? parseInt(m[1], 10) : null;
  }

  open() {
    this.hidden = false;
    document.documentElement.classList.add('rf-noscroll');
    /* next frame, so the transition has a state to move from */
    requestAnimationFrame(() => this.setAttribute('data-open', ''));
    const close = this.querySelector('.rf-drawer__x');
    if (close) close.focus();
  }

  close() {
    this.removeAttribute('data-open');
    document.documentElement.classList.remove('rf-noscroll');
    const done = () => {
      this.hidden = true;
      this.removeEventListener('transitionend', done);
    };
    this.addEventListener('transitionend', done);
    /* if transitions never fire, don't leave it stuck open */
    setTimeout(() => {
      if (!this.hasAttribute('data-open')) this.hidden = true;
    }, 400);
  }
}

if (!customElements.get('rf-cart-drawer')) {
  customElements.define('rf-cart-drawer', RFCartDrawer);
}
